import { Controller } from '@hotwired/stimulus'
import $ from 'jquery'

// Type definitions for select2
declare global {
  interface JQuery {
    select2(options?: Record<string, unknown>): JQuery
    select2(command: string, ...args: unknown[]): unknown
  }
}

// Connects to data-controller="consolidation-tree"
export default class extends Controller {
  private formTarget!: HTMLFormElement
  private selectTarget!: HTMLInputElement
  private submitTarget!: HTMLInputElement
  private chevronTargets!: HTMLElement[]
  private consolidationCompanyIdValue!: string

  static targets = ['form', 'select', 'submit', 'chevron']

  static values = {
    consolidationCompanyId: String,
  }

  setupSelect2(element: HTMLInputElement): void {
    $(element).select2({
      width: '100%',
      ajax: {
        url: window.Silverfin.Routes.autocomplete_companies_path({
          firm_id: window.Silverfin.currentFirmId,
        }),
        dataType: 'json',
        delay: 250,
        data: (params: { term: string }) => ({
          q: params.term,
          consolidation_company_id: this.consolidationCompanyIdValue,
        }),
        processResults: (data: object) => {
          return {
            results: data,
          }
        },
      },
      theme: 'silverfin',
      minimumInputLength: 2,
      templateResult: (company: { name: string }) => company.name,
      templateSelection: (company: { name: string; text: string }) => {
        if (company.text !== '') {
          // placeholder situation
          return company.text
        } else {
          return company.name
        }
      },
    })
  }

  selectTargetConnected(element: HTMLInputElement): void {
    this.setupSelect2(element)

    $(element).on('change', () => {
      if ($(this.selectTarget).find(':selected').val() !== '') {
        this.enableSubmit()
        setTimeout(() => this.submitTarget.focus(), 0)
      } else {
        this.disableSubmit()
      }
    })
  }

  disableSubmit(): void {
    this.submitTarget.classList.add('sf-disabled')
    this.submitTarget.classList.remove('sf-btn-success')
  }

  enableSubmit(): void {
    this.submitTarget.classList.remove('sf-disabled')
    this.submitTarget.classList.add('sf-btn-success')
  }

  toggleChevrons(event: {
    target: HTMLElement
    params: { companyId: string }
  }): void {
    const isCollapsing = event.target.classList.contains('fa-chevron-down')
    const currentRow = $(event.target).closest('tr')

    event.target.classList.toggle('fa-chevron-down')
    event.target.classList.toggle('fa-chevron-right')

    if (isCollapsing) {
      this.hideAllDescendants(currentRow, event.params.companyId)
    } else {
      this.showDirectChildren(currentRow, event.params.companyId)
    }
  }

  private showDirectChildren(parentRow: JQuery, companyId: string): void {
    const allDirectChildren = $(`[data-child-of="${companyId}"]`)
    const parentLevel = this.getRowLevel(parentRow)

    allDirectChildren.each((_, element) => {
      const $child = $(element)
      const childLevel = this.getRowLevel($child)

      if (
        childLevel === parentLevel + 1 &&
        this.isImmediateChild($child, parentRow)
      ) {
        $child.show()
      }
    })
  }

  private isImmediateChild(childRow: JQuery, parentRow: JQuery): boolean {
    let currentRow = parentRow.next('tr')
    const parentLevel = this.getRowLevel(parentRow)

    while (currentRow.length > 0) {
      const currentLevel = this.getRowLevel(currentRow)

      if (currentLevel <= parentLevel) {
        return false
      }

      if (currentRow.is(childRow)) {
        return currentLevel === parentLevel + 1
      }

      currentRow = currentRow.next('tr')
    }

    return false
  }

  private hideAllDescendants(parentRow: JQuery, _companyId: string): void {
    let nextRow = parentRow.next('tr')

    while (nextRow.length > 0 && this.isDescendantOf(nextRow, parentRow)) {
      nextRow.hide()

      const chevronIcon = nextRow.find(
        'i[data-consolidation-tree-company-id-param]'
      )
      if (chevronIcon.length > 0) {
        chevronIcon.removeClass('fa-chevron-down').addClass('fa-chevron-right')
      }

      nextRow = nextRow.next('tr')
    }
  }

  private isDescendantOf(possibleChild: JQuery, ancestor: JQuery): boolean {
    const childLevel = this.getRowLevel(possibleChild)
    const ancestorLevel = this.getRowLevel(ancestor)

    return childLevel > ancestorLevel
  }

  private getRowLevel(row: JQuery): number {
    const level = row.attr('data-level')
    return level ? parseInt(level, 10) : 0
  }
}
